import ClassNames from "classnames";
import { FunctionComponent, useRef, useState } from "react";
import { RemoveScroll } from "react-remove-scroll";
import { CSSTransition } from "react-transition-group";
import headerBaseStyles from "../../stylesheets/components/header-base.module.css";
import styles from "../../stylesheets/components/header-mobile/header-2-mobile.module.css";
import { CommonHeaderLayoutProps } from "../../types/index.js";
import { css } from "../../utils/emotion.js";
import {
  useIntersection,
  useKeyDown,
  useScrollDirection,
} from "../../utils/hooks.js";
import {
  getActionAndContactLinks,
  getBookingEnquiryLinksWithIcon,
  getContactLinks,
  onTransitionEnd,
  overrideImagesAspectRatio,
} from "../../utils/utils.js";
import Icon from "../Icon.js";
import MainMenuListCollapsible, {
  verticalMenuClassNames,
} from "../MainMenuListCollapsible.js";
import ImagesModule from "../Modules/ImagesModule.js";
import SmartLink from "../SmartLink.js";
import HamburgerButton from "./HamburgerButton.js";
import MobileLanguageMenu from "./MobileLanguageMenu.js";
import MobileMainLogo from "./MobileMainLogo.js";

const Header2Mobile: FunctionComponent<CommonHeaderLayoutProps> = ({
  accommodation,
  actionLinks,
  activeModuleId,
  activePagePath,
  fallbackLanguageId,
  imagesModule: imagesModuleProp,
  sticky,
  isPreview,
  languageId,
  logo,
  logoSize,
  mainPageURL,
  menuItems,
  pageId,
  pages,
  queries,
  scheme,
  whatsAppNumber,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const scrollDirection = useScrollDirection();
  const ref = useRef<HTMLDivElement>(null);
  const nodeRef = useRef<HTMLDivElement>(null);
  const intersection = useIntersection(ref);
  const imagesModule = imagesModuleProp
    ? overrideImagesAspectRatio(imagesModuleProp)
    : undefined;

  useKeyDown("Escape", () => setIsMenuOpen(false));

  const isBarVisible =
    !isPreview &&
    (scrollDirection === "up" || intersection?.isIntersecting || sticky);

  const quickNav = getActionAndContactLinks(
    accommodation,
    actionLinks,
    languageId,
    whatsAppNumber,
  );

  const barNav = [
    ...getBookingEnquiryLinksWithIcon(actionLinks),
    ...getContactLinks(accommodation, languageId),
  ];

  return (
    <div className={headerBaseStyles["HeaderModule__Inner--narrow"]}>
      <header
        className={ClassNames(styles.Header2Mobile, {
          [styles["Header2Mobile--preview"]]: isPreview,
        })}
      >
        <div ref={ref} className={styles.Header2Mobile__LogoContainer}>
          <MobileMainLogo
            accommodationName={accommodation.name}
            isPreview={isPreview}
            logo={logo}
            mainPageURL={mainPageURL}
            isSmall={false}
            size={logoSize}
          />
        </div>

        {imagesModule?.pageId && (
          <ImagesModule
            translatedModule={imagesModule}
            isPreview={isPreview}
            pageId={pageId}
            queries={queries}
            isActive={false}
            isInsideHeader={true}
            isFirstOnPage={false}
            parentScheme={scheme}
            activeModuleId={activeModuleId}
            fullHeight={true}
            className={styles.ImagesModule}
          />
        )}

        <div
          className={ClassNames(
            styles.Header2Mobile__Bar,
            { [styles["Header2Mobile__Bar--visible"]]: isBarVisible },
            css({
              background: scheme.main.background,
              color: scheme.main.text,
            }),
          )}
        >
          <div>
            <HamburgerButton
              isMenuOpen={isMenuOpen}
              languageId={languageId}
              onClick={() => setIsMenuOpen((v) => !v)}
            />
          </div>
          <ul className={styles.Header2Mobile__BarNav}>
            {barNav.map(({ href, icon, type }) => (
              <li
                key={type}
                className={ClassNames(styles.Header2Mobile__BarNavItem, {
                  [styles["Header2Mobile__BarNavItem--email"]]:
                    type === "email",
                })}
              >
                <SmartLink
                  isPreview={isPreview}
                  anchorAttrs={{
                    href,
                    className: styles.Header2Mobile__BarNavLink,
                  }}
                >
                  <Icon
                    glyph={icon}
                    className={styles.Header2Mobile__BarNavIcon}
                  />
                </SmartLink>
              </li>
            ))}
          </ul>
        </div>

        <CSSTransition
          in={isMenuOpen}
          timeout={2000}
          mountOnEnter={true}
          unmountOnExit={true}
          addEndListener={(done) => {
            nodeRef.current && onTransitionEnd(nodeRef.current, done);
          }}
          nodeRef={nodeRef}
          classNames={{
            enterActive: styles["Header2Mobile__Menu--open"],
            enterDone: styles["Header2Mobile__Menu--open"],
          }}
        >
          <div
            ref={nodeRef}
            className={ClassNames(
              styles.Header2Mobile__Menu,
              css({
                background: scheme.main.background,
                color: scheme.main.text,
              }),
            )}
          >
            <RemoveScroll
              enabled={!isPreview}
              className={styles.Header2Mobile__MenuInner}
            >
              <HamburgerButton
                className={styles.Header2Mobile__Close}
                isMenuOpen={true}
                languageId={languageId}
                onClick={() => setIsMenuOpen((v) => !v)}
              />

              <MobileLanguageMenu
                className={styles.Header2Mobile__LanguageMenu}
                isPreview={isPreview}
                languageId={languageId}
                pageId={pageId}
                onClick={() => setIsMenuOpen(false)}
              />

              <MainMenuListCollapsible
                isPreview={isPreview}
                languageId={languageId}
                onMenuItemClick={() => setIsMenuOpen(false)}
                menuItems={menuItems}
                pages={pages}
                activePagePath={activePagePath}
                fallbackLanguageId={fallbackLanguageId}
                menuLevel={0}
                collapseLevel={0}
                classNames={verticalMenuClassNames}
              />

              <ul className={styles.Header2Mobile__QuickNav}>
                {quickNav.map(({ href, icon, type }) => (
                  <li key={type}>
                    <SmartLink
                      isPreview={isPreview}
                      anchorAttrs={{
                        href,
                        className: styles.Header2Mobile__QuickNavLink,
                        onClick: () =>
                          type !== "email" &&
                          type !== "phone" &&
                          setIsMenuOpen(false),
                      }}
                    >
                      <Icon
                        glyph={icon}
                        className={styles.Header2Mobile__QuickNavIcon}
                      />
                    </SmartLink>
                  </li>
                ))}
              </ul>
            </RemoveScroll>
          </div>
        </CSSTransition>
      </header>
    </div>
  );
};

export default Header2Mobile;
